import {
  CheckCircleDarkIcon,
  ChevronUpDarkIcon,
  MoreVerticalDarkIcon,
  ReplyArrowDarkIcon,
} from "../../../../assets/icons";
import { Avatar } from "../../../data/Avatar";
import { DropdownButton } from "../../../forms/DropdownButton/DropdownButton";
import { DropdownItem } from "../../../forms/DropdownButton/DropdownItem";
import { DiscussionEditState, DiscussionThreadData } from "../../useDiscussionsPanel";
import { DiscussionReplyItem } from "../DiscussionReplyItem";
import { DiscussionTextInput } from "../DiscussionTextInput";

const UnreadDot = (): JSX.Element => <span className="DiscussionUnreadDot" aria-label="Unread" />;

export interface DiscussionThreadItemProps {
  thread: DiscussionThreadData;
  isOpen: boolean;
  isResolved?: boolean;
  replyDraft: string;
  editState: DiscussionEditState | null;
  currentUserUuid: string;
  canCreate: boolean;
  canResolve?: boolean;
  onToggleOpen: (uuid: string) => void;
  onReplyDraftChange: (threadUuid: string, value: string) => void;
  onSaveReply: (threadUuid: string) => void;
  onCancelReply: (threadUuid: string) => void;
  onStartEditThread: (threadUuid: string, content: string) => void;
  onStartEditReply: (threadUuid: string, replyUuid: string, content: string) => void;
  onEditDraftChange: (value: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onDeleteThread: (threadUuid: string) => void;
  onDeleteReply: (threadUuid: string, replyUuid: string) => void;
  onResolveThread: (threadUuid: string) => void;
}

export const DiscussionThreadItem = ({
  thread,
  isOpen,
  isResolved = false,
  replyDraft,
  editState,
  currentUserUuid,
  canCreate,
  canResolve = true,
  onToggleOpen,
  onReplyDraftChange,
  onSaveReply,
  onCancelReply,
  onStartEditThread,
  onStartEditReply,
  onEditDraftChange,
  onSaveEdit,
  onCancelEdit,
  onDeleteThread,
  onDeleteReply,
  onResolveThread,
}: DiscussionThreadItemProps): JSX.Element => {
  const isEditingThread = editState?.type === "thread" && editState.threadUuid === thread.uuid;

  const hasReplies = thread.replies.length > 0;
  const replyCount = thread.replies.length;

  const noOtherUserMessagesAfterThread = thread.replies.every((r) => r.authorUuid === currentUserUuid);
  const canEditOrDeleteThread = thread.authorUuid === currentUserUuid && noOtherUserMessagesAfterThread;

  const showResolve = thread.authorUuid === currentUserUuid && !isResolved && canResolve;
  const showMenu = !isResolved && (canEditOrDeleteThread || showResolve);

  return (
    <div className={`DiscussionThreadItem ${isResolved ? "DiscussionThreadItem_resolved" : ""}`}>
      <Avatar name={thread.authorName} />

      <div className="DiscussionThreadItem__body">
        {/* Header row */}
        <div className="DiscussionThreadItem__header">
          <div className="DiscussionsMeta__row">
            <span className="DiscussionsMeta__author">{thread.authorName}</span>
            <span className="DiscussionsMeta__date">{thread.date}</span>
          </div>
          <div className="DiscussionThreadItem__headerActions">
            {isResolved && (
              <span className="DiscussionThreadItem__checkIcon">
                <CheckCircleDarkIcon />
              </span>
            )}
            {showMenu && (
              <DropdownButton component={<MoreVerticalDarkIcon />} ariaLabel="Thread options">
                {canEditOrDeleteThread && (
                  <DropdownItem onClick={() => onStartEditThread(thread.uuid, thread.content)}>
                    Edit message
                  </DropdownItem>
                )}
                {showResolve && (
                  <DropdownItem onClick={() => onResolveThread(thread.uuid)}>Resolve thread</DropdownItem>
                )}
                {canEditOrDeleteThread && (
                  <DropdownItem onClick={() => onDeleteThread(thread.uuid)}>Delete thread</DropdownItem>
                )}
              </DropdownButton>
            )}
          </div>
        </div>

        {/* Message content or inline edit */}
        {isEditingThread ? (
          <DiscussionTextInput
            value={editState.draft}
            placeholder="Edit your message"
            onChange={onEditDraftChange}
            onSave={onSaveEdit}
            onCancel={onCancelEdit}
          />
        ) : (
          <div className="DiscussionThreadItem__content">
            <div className="DiscussionThreadItem__messageRow">
              {thread.isUnread && <UnreadDot />}
              <p className="DiscussionsMeta__message">{thread.content}</p>
            </div>

            {/* Actions row */}
            <div className="DiscussionThreadItem__actions">
              {canCreate && !isResolved && (
                <button
                  className="DiscussionThreadItem__replyBtn"
                  onClick={() => onToggleOpen(thread.uuid)}
                  aria-label="Reply to thread"
                >
                  <ReplyArrowDarkIcon />
                </button>
              )}
              {hasReplies && (
                <button className="DiscussionThreadItem__replyCount" onClick={() => onToggleOpen(thread.uuid)}>
                  {replyCount} {replyCount === 1 ? "reply" : "replies"}
                  {thread.replies.some((r) => r.isUnread) && <UnreadDot />}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Expanded replies block */}
        {isOpen && !isEditingThread && (
          <div className="DiscussionThreadItem__repliesBlock">
            <button className="DiscussionThreadItem__hideRepliesBtn" onClick={() => onToggleOpen(thread.uuid)}>
              <ChevronUpDarkIcon />
              Hide replies
            </button>

            {thread.replies.length > 0 && (
              <div className="DiscussionThreadItem__replies">
                {thread.replies.map((reply, i) => {
                  const noOtherUserMessagesAfter = thread.replies
                    .slice(i + 1)
                    .every((r) => r.authorUuid === currentUserUuid);
                  const canEditOrDeleteReply = reply.authorUuid === currentUserUuid && noOtherUserMessagesAfter;
                  return (
                    <DiscussionReplyItem
                      key={reply.uuid}
                      reply={reply}
                      threadUuid={thread.uuid}
                      editState={editState}
                      canEditOrDelete={canEditOrDeleteReply}
                      isResolved={isResolved}
                      onStartEditReply={onStartEditReply}
                      onEditDraftChange={onEditDraftChange}
                      onSaveEdit={onSaveEdit}
                      onCancelEdit={onCancelEdit}
                      onDeleteReply={onDeleteReply}
                    />
                  );
                })}
              </div>
            )}

            {canCreate && !isResolved && (
              <DiscussionTextInput
                value={replyDraft}
                placeholder="Reply"
                onChange={(value) => onReplyDraftChange(thread.uuid, value)}
                onSave={() => onSaveReply(thread.uuid)}
                onCancel={() => onCancelReply(thread.uuid)}
                saveLabel="Save"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
